import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CompleteOnboardingInput,
  completeOnboardingSchema,
  type TeamSize,
  teamSizeOptions,
} from '@sea-snag/shared';
import { Badge, Button, Input, Label } from '@sea-snag/ui';
import { HTTPError } from 'ky';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAuth } from '../../hooks/use-auth';
import { setOrganization } from '../../stores/org-store';

const teamSizeLabels: Record<TeamSize, string> = {
  'just-me': 'Just me',
  '2-5': '2–5',
  '6-20': '6–20',
  '21-50': '21–50',
  '51+': '51+',
};

interface StepMainInfoProps {
  email: string;
  token: string;
  onComplete: (orgPid: string) => void;
}

export function StepMainInfo({ email, token, onComplete }: StepMainInfoProps) {
  const { completeOnboarding } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CompleteOnboardingInput>({
    resolver: zodResolver(completeOnboardingSchema),
    defaultValues: {
      token,
    },
  });

  const selectedTeamSize = watch('teamSize');

  const onSubmit = async (data: CompleteOnboardingInput) => {
    setError(null);
    try {
      const result = await completeOnboarding({
        token: data.token,
        name: data.name,
        organizationName: data.organizationName,
        teamSize: data.teamSize,
        password: data.password,
      });
      setOrganization({
        pid: result.organization.id,
        name: result.organization.name,
        slug: result.organization.slug,
        setupCompletedAt: null,
      });
      onComplete(result.organization.id);
    } catch (err) {
      if (err instanceof HTTPError) {
        const body = (await err.response.json().catch(() => ({}))) as { message?: string };
        setError(body.message || 'Something went wrong');
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" aria-label="Account setup form">
      {error && (
        <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Email display */}
      <div className="space-y-2">
        <Label>Email</Label>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-sm">
            {email}
          </Badge>
        </div>
      </div>

      {/* Name */}
      <div className="space-y-2">
        <Label htmlFor="name">Your name</Label>
        <Input
          id="name"
          type="text"
          placeholder="John Doe"
          autoFocus
          aria-describedby={errors.name ? 'name-error' : undefined}
          {...register('name')}
        />
        {errors.name && (
          <p id="name-error" className="text-sm text-destructive">
            {errors.name.message}
          </p>
        )}
      </div>

      {/* Organization name */}
      <div className="space-y-2">
        <Label htmlFor="organizationName">Organization name</Label>
        <Input
          id="organizationName"
          type="text"
          placeholder="Acme Charter"
          aria-describedby={errors.organizationName ? 'org-error' : undefined}
          {...register('organizationName')}
        />
        {errors.organizationName && (
          <p id="org-error" className="text-sm text-destructive">
            {errors.organizationName.message}
          </p>
        )}
      </div>

      {/* Team size */}
      <div className="space-y-2">
        <Label>Team size</Label>
        <div className="grid grid-cols-5 gap-2">
          {teamSizeOptions.map((size) => (
            <button
              key={size}
              type="button"
              onClick={() => setValue('teamSize', size, { shouldValidate: true })}
              className={`rounded-lg border-2 px-2 py-2.5 text-center text-sm font-medium transition-all ${
                selectedTeamSize === size
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              {teamSizeLabels[size]}
            </button>
          ))}
        </div>
        {errors.teamSize && <p className="text-sm text-destructive">{errors.teamSize.message}</p>}
      </div>

      <input type="hidden" {...register('token')} />

      {/* Password */}
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          placeholder="Create a password"
          aria-describedby={errors.password ? 'password-error' : 'password-hint'}
          {...register('password')}
        />
        {errors.password && (
          <p id="password-error" className="text-sm text-destructive">
            {errors.password.message}
          </p>
        )}
        <p id="password-hint" className="text-xs text-muted-foreground">
          Must be at least 8 characters
        </p>
      </div>

      {/* Confirm password */}
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirm password</Label>
        <Input
          id="confirmPassword"
          type="password"
          placeholder="Confirm your password"
          aria-describedby={errors.confirmPassword ? 'confirm-error' : undefined}
          {...register('confirmPassword')}
        />
        {errors.confirmPassword && (
          <p id="confirm-error" className="text-sm text-destructive">
            {errors.confirmPassword.message}
          </p>
        )}
      </div>

      <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
        {isSubmitting ? 'Creating account...' : 'Continue'}
      </Button>
    </form>
  );
}
